package ui.campaign.detailv2.editor.widget

import androidx.compose.animation.AnimatedContent
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.mohamedrejeb.richeditor.ui.material3.RichText
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Chat_question
import compose.assets.svg.Close
import io.Repository
import kotlinx.datetime.isoDayNumber
import org.ssoft.common.type.ScreenRatio
import ssoft.frontend.generated.resources.*
import theme.*
import tools.formatFromRes
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.feature.FeatureCounter
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperELocation
import ui.widget.*

@Stable
private class InfoItem(
    val color: Color,
    val label: String,
    val shape: Shape
)

private val infoItems by lazy {
    listOf(
        InfoItem(ColorMaterialGreen500, "0 - 20%", CircleShape),
        InfoItem(ColorMaterialBlue500, "20 - 60%", CircleShape),
        InfoItem(ColorMaterialViolet500, "60 - 90%", CircleShape),
        InfoItem(ColorMaterialOrange500, "90 - 100%", CircleShape),
        InfoItem(ColorMaterialRed500, "100+%", CircleShape),
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CampaignEditorScope.DialogActiveCampaigns(
    state: DialogState,
    selectedCampaignDefinition: MutableState<FeatureCounter.DayInfo?>,
    device: CampaignWrapperELocation.AdapterDevice.Item
) {

    selectedCampaignDefinition.value
        ?.takeIf { state.visible }
        ?.also { def ->
            val campaigns = remember {
                mutableStateOf<Io.ListingCampaignDeviceDoyCheck?>(null)
            }
            val screenRatio = ScreenRatio[device.data.screenRatio].let {
                it.pair.first.toFloat() / it.pair.second.toFloat()
            }
            LaunchedEffect(device.data.id, def.date.dayOfYear) {
                campaigns.value = runCatching {
                    Repository.api.campaign.devicesCheckDeviceDoy(
                        companyId = args.companyId,
                        campaignId = args.campaignId,
                        doy = def.date.dayOfYear,
                        deviceId = device.data.id
                    )
                }.getOrNull()
            }

            Dialog(state = state) {
                DialogUI(
                    modifier = Modifier.fillMaxWidth().align(Alignment.Center),
                    title = { Text(def.date.formatFromRes()) },
                    dismissButton = { TextButton({ state.hide() }) { Text(Res.string.dialog__action_close.rememberString()) } },
                    onDismissRequest = { state.hide() }) {

                    AnimatedContent(campaigns.value) {
                        if (it == null) {
                            Spacer(Modifier.fillMaxWidth())
                        } else {
                            FlowRow(
                                modifier = Modifier.fillMaxWidth()
                                    .wrapContentHeight(align = Alignment.Top)
                                    .verticalScroll(rememberScrollState()),
                                horizontalArrangement = Arrangement.SpaceAround,
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                campaigns.value?.items?.also { items ->
                                    items.forEach {
                                        Box(
                                            Modifier.let {
                                                if (items.size < 3) it.width(192.dp)
                                                else it.width(128.dp)
                                            }.aspectRatio(screenRatio)
                                        ) {
                                            LazyImage(
                                                id = it.visual,
                                                alphaBackground = false,
                                                modifier = Modifier.fillMaxSize()
                                            )
                                            Text(
                                                text = it.campaignName,
                                                style = MaterialTheme.typography.labelMedium,
                                                maxLines = 2,
                                                overflow = TextOverflow.Ellipsis,
                                                modifier = Modifier.align(Alignment.BottomCenter)
                                                    .fillMaxWidth()
                                                    .background(Color.Black.copy(alpha = 0.8F))
                                                    .padding(4.dp)

                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
}

@Composable
fun CampaignEditorScope.DialogDeviceHeatMap(
    state: DialogState,
    device: CampaignWrapperELocation.AdapterDevice.Item,
    ohSpace: FeatureCounter.LocationTimeOH
) {
    val selectedCampaignDefinition = remember {
        mutableStateOf<FeatureCounter.DayInfo?>(null)
    }
    val activeCampaignsDialog = DialogState.remember(key = "active-campaigns-info")
    DialogActiveCampaigns(
        state = activeCampaignsDialog,
        selectedCampaignDefinition = selectedCampaignDefinition,
        device = device
    )

    Dialog(
        state = state,
        background = MaterialTheme.colorScheme.background,
        dismissOnTouchOutside = false
    ) {
        if (state.visible) {
            Column {
                Toolbar(device, state)
                HeatMapUI(
                    activeBackground = {
                        device.usageByDoy[it.dayOfYear]?.inCampaignRange == true
                    },
                    dayColor = {
                        device.usageByDoy[it.dayOfYear]
                            ?.takeIf { it.usageNow > 0F }
                            ?.let { FeatureCounter.computeColor(it.usageFuture * 100F) }
                            ?: Color.Transparent
                    },
                    onClickAction = {
                        device.usageByDoy[it.dayOfYear]
                            ?.takeIf { it.usageNow > 0F }
                            ?.let { definition ->
                                {
                                    selectedCampaignDefinition.value = definition
                                    activeCampaignsDialog.show()
                                }
                            }
                    },
                    showCrossOverlay = {
                        ohSpace.daySpecsByIsoWeekDay[it.dayOfWeek.isoDayNumber]?.enabled != true
                    },
                    modifier = Modifier.fillMaxSize()
                )

            }
        }
    }
}

@Composable
private fun Toolbar(device: CampaignWrapperELocation.AdapterDevice.Item, state: DialogState) {
    val tooltipDialogState = DialogState.remember(key = "tooltip")
    Row(
        Modifier
            .background(MaterialTheme.colorScheme.surfaceContainerLow)
            .padding(8.dp)
    ) {
        FloatingActionButton({ state.hide() }) {
            Icon(Svg.Close, null)
        }
        Text(
            text = device.data.name,
            style = MaterialTheme.typography.headlineMedium,
            modifier = Modifier.align(Alignment.CenterVertically)
                .padding(start = 8.dp)
                .weight(1F)
        )

        IconButton(
            onClick = { tooltipDialogState.show() },
            modifier = Modifier.align(Alignment.CenterVertically)
        ) {
            Icon(Svg.Chat_question, null)
        }
    }
    TooltipDialog(tooltipDialogState)
}

@Composable
private fun TooltipDialog(tooltipDialogState: DialogState) {
    Dialog(
        state = tooltipDialogState
    ) {
        if (tooltipDialogState.visible) {
            DialogUI(
                modifier = Modifier.align(Alignment.Center),
                dismissButton = { TextButton({ tooltipDialogState.hide() }) { Text(Res.string.dialog__action_close.rememberString()) } },
                onDismissRequest = { tooltipDialogState.hide() }) {

                val richTextState = tools.rememberRichTextState(
                    Res.string.layer_top_detail_editor__location_hm_tooltip
                )

                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    RichText(state = richTextState)
                    Space(4.dp)
                    PercentageItem(
                        label = Res.string.layer_top_detail_editor__location_hm_tooltip_period.rememberString(),
                        color = primaryContainerDark
                    )
                    PercentageItem(
                        label = Res.string.layer_top_detail_editor__location_hm_tooltip_excluded.rememberString(),
                        color = surfaceContainerLowDark,
                        markerModifier = Modifier.drawWithContent {
                            drawLine(
                                color = Color.Gray.copy(0.8F),
                                start = androidx.compose.ui.geometry.Offset(0F, size.height),
                                end = androidx.compose.ui.geometry.Offset(size.width, 0F),
                                strokeWidth = 1F
                            )
                        })
                    Space(4.dp)
                    infoItems.forEach {
                        PercentageItem(it)
                    }
                }
            }
        }
    }
}

@Composable
private fun PercentageItem(item: InfoItem) {
    PercentageItem(item.label, item.color, item.shape)
}

@Composable
private fun PercentageItem(
    label: String,
    color: Color,
    shape: Shape = RoundedCornerShape(16),
    markerModifier: Modifier = Modifier
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Box(
            modifier = Modifier
                .align(Alignment.CenterVertically)
                .size(24.dp)
                .background(
                    color = Color.White,
                    shape = shape
                )
                .padding(1.dp)
                .background(
                    color = color,
                    shape = shape
                ).then(markerModifier)
        )
        Text(
            text = label,
            modifier = Modifier.weight(1F)
                .align(Alignment.CenterVertically),
        )
    }
}


